/**
 * SubmittedTicketCard — success state shown after the agent calls submit_ticket.
 *
 * Replaces the DraftTicketCard in the right pane (and inline in chat)
 * once a draft has been submitted. The visual emphasis shifts from
 * "draft, awaiting confirmation" to "logged successfully — here's the
 * ticket ID and where to find it next".
 *
 * Includes:
 *   - Animated success ring + checkmark in the header.
 *   - Prominent ticket ID (monospace, copyable).
 *   - Direct link to the Asana task if the backend returned one.
 *   - All the original fields, presented as a record (read-only).
 */

import type { CSSProperties, ReactNode } from 'react'
import { CheckCircle2, ExternalLink, User } from 'lucide-react'
import type { SubmittedTicketPayload } from '../types'
import {
  formatTicketType,
  ticketTypeAccent,
  severityAccent,
  formatSeverityLabel,
} from './ticket-visuals'

interface SubmittedTicketCardProps {
  data: SubmittedTicketPayload
  variant?: 'full' | 'compact'
  onLogAnother?: () => void
}

export function SubmittedTicketCard({
  data,
  variant = 'full',
  onLogAnother,
}: SubmittedTicketCardProps): ReactNode {
  const isFull = variant === 'full'
  const typeAccent = ticketTypeAccent(data.type)
  const sevAccent = severityAccent(data.severity)

  return (
    <div
      style={{
        ...styles.card,
        ...(isFull ? styles.cardFull : styles.cardCompact),
      }}
      data-testid={`submitted-ticket-card-${variant}`}
    >
      {/* Success rail across top */}
      <div style={styles.successRail} />

      {/* Hero: checkmark + ticket id */}
      <div style={isFull ? styles.heroFull : styles.heroCompact}>
        <div style={styles.checkRing}>
          <CheckCircle2
            size={isFull ? 30 : 20}
            color="var(--success)"
            strokeWidth={2.2}
          />
        </div>
        <div style={styles.heroText}>
          <span style={styles.eyebrow}>Ticket Submitted</span>
          <span
            style={{
              ...styles.ticketId,
              ...(isFull ? styles.ticketIdFull : styles.ticketIdCompact),
            }}
            data-testid="submitted-ticket-id"
          >
            {data.ticket_id}
          </span>
        </div>
      </div>

      {/* Pills */}
      <div style={styles.pillRow}>
        <span
          style={{
            ...styles.pill,
            color: typeAccent.fg,
            background: typeAccent.bg,
            border: `1px solid ${typeAccent.border}`,
          }}
        >
          <span style={{ ...styles.pillDot, background: typeAccent.fg }} />
          {formatTicketType(data.type)}
        </span>
        <span
          style={{
            ...styles.pill,
            color: sevAccent.fg,
            background: sevAccent.bg,
            border: `1px solid ${sevAccent.border}`,
          }}
        >
          {formatSeverityLabel(data.severity)}
        </span>
        {data.status ? (
          <span style={styles.statusPill}>{data.status.replace(/_/g, ' ')}</span>
        ) : null}
      </div>

      {/* Title */}
      <h2 style={isFull ? styles.titleFull : styles.titleCompact}>{data.title}</h2>

      {/* Description (read-only echo of what was submitted) */}
      <div
        style={{
          ...styles.descriptionBlock,
          ...(isFull ? styles.descriptionFull : styles.descriptionCompact),
        }}
      >
        <span style={styles.sectionLabel}>Description</span>
        <p
          style={{
            ...styles.descriptionText,
            ...(isFull ? styles.descriptionTextFull : {}),
          }}
        >
          {data.description}
        </p>
      </div>

      {/* Metadata */}
      <div style={styles.metaGrid}>
        <MetaItem label="Entity" value={data.entity_id} mono />
        {data.affected_area ? (
          <MetaItem label="Affected area" value={data.affected_area} mono />
        ) : null}
        <MetaItem
          label="Reporter"
          value={
            data.reporter ? (
              <span style={styles.reporter}>
                <User size={12} style={styles.reporterIcon} />
                {data.reporter}
              </span>
            ) : (
              <span style={styles.metaEmpty}>—</span>
            )
          }
        />
        <MetaItem
          label="Tags"
          value={
            data.tags && data.tags.length > 0 ? (
              <span style={styles.tagRow}>
                {data.tags.map((tag) => (
                  <span key={tag} style={styles.tagChip}>
                    {tag}
                  </span>
                ))}
              </span>
            ) : (
              <span style={styles.metaEmpty}>—</span>
            )
          }
        />
      </div>

      {/* Footer actions */}
      <div style={styles.footer}>
        {data.asana_url ? (
          <a
            href={data.asana_url}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkAction}
            data-testid="submitted-ticket-asana-link"
          >
            <ExternalLink size={13} />
            Open in Asana
          </a>
        ) : (
          <span />
        )}
        {onLogAnother ? (
          <button
            type="button"
            style={styles.primaryAction}
            onClick={onLogAnother}
            data-testid="log-another-button"
          >
            Log another
          </button>
        ) : null}
      </div>
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function MetaItem({
  label,
  value,
  mono = false,
}: {
  label: string
  value: ReactNode
  mono?: boolean
}) {
  return (
    <div style={styles.metaItem}>
      <span style={styles.metaLabel}>{label}</span>
      <span
        style={{
          ...styles.metaValue,
          ...(mono ? styles.metaValueMono : {}),
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const SUCCESS_TINT = 'rgba(34, 197, 94, 0.32)'

const styles: Record<string, CSSProperties> = {
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg-secondary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    boxShadow: `0 4px 18px rgba(0, 0, 0, 0.18), 0 0 0 1px ${SUCCESS_TINT}`,
  },
  cardFull: {
    gap: '20px',
    padding: '24px',
  },
  cardCompact: {
    gap: '12px',
    padding: '14px',
  },
  successRail: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background:
      'linear-gradient(90deg, transparent 0%, var(--success) 50%, transparent 100%)',
    opacity: 0.85,
  },
  heroFull: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  heroCompact: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  checkRing: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background:
      'linear-gradient(135deg, color-mix(in srgb, var(--success) 18%, transparent) 0%, color-mix(in srgb, var(--success) 6%, transparent) 100%)',
    width: '52px',
    height: '52px',
    borderRadius: '50%',
    border: '1px solid color-mix(in srgb, var(--success) 36%, transparent)',
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  eyebrow: {
    fontSize: '10.5px',
    fontWeight: 700,
    color: 'var(--success)',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  ticketId: {
    fontFamily: "'JetBrains Mono', monospace",
    color: 'var(--text-primary)',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ticketIdFull: {
    fontSize: '20px',
  },
  ticketIdCompact: {
    fontSize: '14px',
  },
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  },
  pillDot: {
    display: 'inline-block',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
  },
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
  },
  titleFull: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  },
  titleCompact: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 600,
    lineHeight: 1.3,
    color: 'var(--text-primary)',
  },
  descriptionBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  descriptionFull: {
    padding: '14px 16px',
  },
  descriptionCompact: {
    padding: '10px 12px',
  },
  sectionLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  descriptionText: {
    margin: 0,
    fontSize: '13px',
    lineHeight: 1.55,
    color: 'var(--text-primary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  descriptionTextFull: {
    fontSize: '14px',
    lineHeight: 1.65,
  },
  metaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px 20px',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  metaLabel: {
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  metaValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  metaValueMono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '12px',
  },
  metaEmpty: {
    color: 'var(--text-muted)',
  },
  reporter: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  reporterIcon: {
    color: 'var(--text-muted)',
  },
  tagRow: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '6px',
    whiteSpace: 'normal',
  },
  tagChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    fontSize: '11px',
    color: 'var(--text-secondary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '999px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    paddingTop: '12px',
    borderTop: '1px solid var(--border-subtle)',
  },
  linkAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--accent-primary)',
    background: 'color-mix(in srgb, var(--accent-primary) 8%, transparent)',
    borderRadius: 'var(--radius-sm)',
    textDecoration: 'none',
    transition: 'background var(--transition-fast)',
  },
  primaryAction: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '7px 14px',
    fontSize: '12px',
    fontWeight: 600,
    color: 'white',
    background: 'var(--accent-primary)',
    border: 'none',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    transition: 'background var(--transition-fast)',
  },
}

export default SubmittedTicketCard
